import type { RegistrationPaymentSummary } from '../../services/booking-registration-api.service';
import type { NamePrefixOptionLike } from '../bookings/utils/name-prefix.util';
import { resolvePartyPersonDisplayName } from '../bookings/utils/name-prefix.util';

export const REGISTRATION_PAYMENT_SUBMITTED_FOR_APPROVAL_MESSAGE =
  'Your payment has been submitted for approval. The paid and received amounts will be updated only after the payment is approved.';

export const REGISTRATION_PAYMENT_APPROVAL_PENDING_BLOCKED_MESSAGE =
  'Payment approval is pending. You cannot make this payment until it is approved.';

export function isRegistrationPaymentPendingApproval(
  status: string | null | undefined,
): boolean {
  return String(status ?? '').trim().toUpperCase() === 'PENDING';
}

export function isRegistrationChargePaymentType(
  paymentType: string | null | undefined,
): boolean {
  return String(paymentType ?? 'REGISTRATION').trim().toUpperCase() === 'REGISTRATION';
}

export interface RegistrationPaymentDisplayDetails {
  unitAmount: string;
  agreementValue: string;
  stampDutyPercentage: string;
  stampDutyAmount: string;
  registrationFeePercentage: string;
  registrationFeeAmount: string;
  totalAmount: string;
  paidAmount: string;
  pendingAmount: string;
  paymentStatus: string;
}

type FallbackUnitAmount = string | number | null;

function formatRegistrationInr(value: number | null | undefined): string {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) {
    return '-';
  }
  return `₹ ${amount.toLocaleString('en-IN')}`;
}

function formatFallbackUnitAmount(fallbackUnitAmount?: FallbackUnitAmount): string {
  if (fallbackUnitAmount == null || fallbackUnitAmount === '' || fallbackUnitAmount === '-') {
    return '-';
  }
  if (typeof fallbackUnitAmount === 'number') {
    return formatRegistrationInr(fallbackUnitAmount);
  }
  return String(fallbackUnitAmount);
}

function resolveUnitAmountDisplay(
  summary: RegistrationPaymentSummary,
  fallbackUnitAmount?: FallbackUnitAmount,
): string {
  if (summary.unitAmount > 0) {
    return formatRegistrationInr(summary.unitAmount);
  }
  return formatFallbackUnitAmount(fallbackUnitAmount);
}

function pickNumber(source: Record<string, unknown>, ...keys: string[]): number {
  for (const key of keys) {
    const value = source[key];
    if (value == null || value === '') {
      continue;
    }
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }
  return 0;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function pickString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string') {
      continue;
    }
    const trimmed = value.trim();
    if (!trimmed || trimmed === '-' || trimmed === '—' || trimmed === '- -') {
      continue;
    }
    return trimmed;
  }
  return '';
}

function coerceScalarToString(value: unknown): string | null {
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return null;
}

function pickScalarString(...values: unknown[]): string {
  for (const value of values) {
    const coerced = coerceScalarToString(value);
    if (coerced !== null && coerced !== '') {
      return coerced;
    }
  }
  return '';
}

export function resolvePartiesFromBookingApi(
  apiData: Record<string, unknown>,
): Record<string, unknown>[] {
  const parties = apiData['customerDetailParties'];
  let resolved: Record<string, unknown>[] = [];

  if (Array.isArray(parties) && parties.length > 0) {
    resolved = parties as Record<string, unknown>[];
  } else {
    const customerDetails = apiData['customerDetails'];
    if (Array.isArray(customerDetails) && customerDetails.length > 0) {
      resolved = customerDetails as Record<string, unknown>[];
    } else if (customerDetails && typeof customerDetails === 'object') {
      resolved = [customerDetails as Record<string, unknown>];
    } else {
      const summary = asRecord(apiData['summary']) ?? {};
      const owner = asRecord(summary['owner']) ?? {};
      if (owner['name'] ?? owner['email'] ?? owner['mobile']) {
        resolved = [
          {
            partyType: 'owner',
            fullName: owner['name'],
            email: owner['email'],
            mobile: owner['mobile'],
            mobileNo: owner['mobile'],
            panNumber: owner['panMasked'],
          },
        ];
      } else {
        const primaryCustomer = asRecord(apiData['primaryCustomer']) ?? {};
        if (
          primaryCustomer['mobile'] ??
          primaryCustomer['email'] ??
          primaryCustomer['displayName']
        ) {
          resolved = [
            {
              partyType: 'owner',
              fullName: primaryCustomer['displayName'] ?? primaryCustomer['name'],
              email: primaryCustomer['email'],
              mobile: primaryCustomer['mobile'],
              mobileNo: primaryCustomer['mobile'],
            },
          ];
        }
      }
    }
  }

  return sortPartiesOwnerFirst(resolved);
}

function normalizePartyType(value: unknown): 'owner' | 'co_owner' {
  const normalized = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
  return normalized === 'owner' ? 'owner' : 'co_owner';
}

function sortPartiesOwnerFirst(
  parties: Record<string, unknown>[],
): Record<string, unknown>[] {
  return [...parties].sort((a, b) => {
    const aOwner = normalizePartyType(a['partyType']) === 'owner';
    const bOwner = normalizePartyType(b['partyType']) === 'owner';
    if (aOwner && !bOwner) {
      return -1;
    }
    if (!aOwner && bOwner) {
      return 1;
    }
    return 0;
  });
}

function resolveOwnerDisplayName(
  party: Record<string, unknown>,
  summary: Record<string, unknown>,
  primaryCustomer: Record<string, unknown>,
  summaryOwner: Record<string, unknown>,
  isOwner: boolean,
  projectName: string,
  prefixOptions?: NamePrefixOptionLike[],
): string {
  let ownerName = resolvePartyPersonDisplayName(party, {
    isOwner,
    summaryOwner,
    primaryCustomer,
    prefixOptions,
    fallbackNames: [isOwner ? summary['primaryOwnerName'] : ''],
  });

  if (
    ownerName &&
    ownerName !== '—' &&
    projectName &&
    ownerName.includes(projectName) &&
    isOwner &&
    typeof summaryOwner['name'] === 'string'
  ) {
    const summaryOwnerName = summaryOwner['name'].trim();
    if (summaryOwnerName) {
      ownerName = resolvePartyPersonDisplayName(
        { ...party, fullName: summaryOwnerName, name: summaryOwnerName },
        { isOwner, summaryOwner, primaryCustomer, prefixOptions },
      );
    }
  }

  return ownerName === '—' ? '' : ownerName;
}

/** Exported for booking overview / owner-summary contact resolution. */
export function pickContactString(...values: unknown[]): string {
  return pickString(...values);
}

export interface RegistrationOwnerDisplay {
  partyType: string;
  name: string | null;
  mobileNo: string | null;
  email: string | null;
  panNumber: string | null;
}

export function mapOwnersFromBookingApi(
  apiData: unknown,
  prefixOptions?: NamePrefixOptionLike[],
): RegistrationOwnerDisplay[] {
  const data = asRecord(apiData);
  if (!data) {
    return [];
  }

  const summary = asRecord(data['summary']) ?? {};
  const summaryOwner = asRecord(summary['owner']) ?? {};
  const primaryCustomer = asRecord(data['primaryCustomer']) ?? {};
  const projectName = pickString(summary['projectName']);
  const parties = resolvePartiesFromBookingApi(data);

  if (parties.length > 0) {
    return parties.map((party) => {
      const partyType = normalizePartyType(party['partyType']);
      const isOwner = partyType === 'owner';
      const kyc = asRecord(party['kyc']) ?? {};
      const contact = asRecord(kyc['contact']) ?? {};
      const pan = asRecord(kyc['pan']) ?? {};

      return {
        partyType,
        name:
          resolveOwnerDisplayName(
            party,
            summary,
            primaryCustomer,
            summaryOwner,
            isOwner,
            projectName,
            prefixOptions,
          ) || null,
        mobileNo:
          pickString(
            party['mobileNo'],
            party['mobile'],
            party['phoneNumber'],
            contact['mobile'],
            isOwner ? summaryOwner['mobile'] : '',
            isOwner ? primaryCustomer['mobile'] : '',
          ) || null,
        email:
          pickString(
            party['email'],
            party['emailId'],
            contact['email'],
            isOwner ? summaryOwner['email'] : '',
            isOwner ? primaryCustomer['email'] : '',
          ) || null,
        panNumber:
          pickString(
            party['panNumber'],
            pan['panNumber'],
            kyc['panNumber'],
            party['panMasked'],
            isOwner ? summaryOwner['panMasked'] : '',
            isOwner ? summaryOwner['panNumber'] : '',
          ) || null,
      };
    });
  }

  const ownerName = resolveOwnerDisplayName(
    {},
    summary,
    primaryCustomer,
    summaryOwner,
    true,
    projectName,
    prefixOptions,
  );

  return [
    {
      partyType: 'owner',
      name: ownerName || null,
      mobileNo:
        pickString(
          summaryOwner['mobile'],
          summaryOwner['mobileNo'],
          primaryCustomer['mobile'],
        ) || null,
      email: pickString(summaryOwner['email'], primaryCustomer['email']) || null,
      panNumber:
        pickString(summaryOwner['panMasked'], summaryOwner['panNumber']) || null,
    },
  ];
}

export interface RegistrationApplicantResidential {
  addressLine?: string;
  city?: string;
  state?: string;
  country?: string;
  pinCode?: string;
}

export interface RegistrationApplicantDisplay {
  partyType: 'owner' | 'co_owner';
  name: string;
  mobile: string;
  email: string;
  pan: string;
  aadhar: string;
  nationality: string;
  residential: RegistrationApplicantResidential;
  residentialDetails: string;
}

function residentialInputFromParty(party: Record<string, unknown>): RegistrationApplicantResidential {
  const kyc = asRecord(party['kyc']) ?? {};
  const residential =
    asRecord(party['residential']) ?? asRecord(kyc['residential']) ?? {};
  return {
    addressLine: pickString(residential['addressLine'], party['addressLine']),
    city: pickString(residential['city'], party['residentialCity']),
    state: pickString(residential['state'], party['residentialState']),
    country: pickString(residential['country'], party['residentialCountry']),
    pinCode: pickString(residential['pinCode'], residential['pin']),
  };
}

function aadharFromParty(
  party: Record<string, unknown>,
  summaryOwner: Record<string, unknown>,
): string {
  const kyc = asRecord(party['kyc']) ?? {};
  const aadhaar = asRecord(kyc['aadhaar']) ?? {};
  return (
    pickString(
      aadhaar['aadhaarNumber'],
      kyc['aadhaarNumber'],
      party['aadhaarNumber'],
      party['aadharNumber'],
      summaryOwner['aadhaarMasked'],
    ) ?? '—'
  );
}

function nationalityFromParty(party: Record<string, unknown>): string {
  const kyc = asRecord(party['kyc']) ?? {};
  const identity = asRecord(kyc['identity']) ?? {};
  const personal = asRecord(kyc['personal']) ?? {};
  const aadhaar = asRecord(kyc['aadhaar']) ?? {};
  return pickString(
    identity['nationality'],
    personal['nationality'],
    aadhaar['nationality'],
    party['nationality'],
  );
}

export function mapRegistrationApplicantsFromApi(
  apiData: unknown,
  prefixOptions?: NamePrefixOptionLike[],
): RegistrationApplicantDisplay[] {
  const emptyApplicant = (): RegistrationApplicantDisplay => ({
    partyType: 'owner',
    name: '-',
    mobile: '-',
    email: '-',
    pan: '-',
    aadhar: '—',
    nationality: '',
    residential: {},
    residentialDetails: '—',
  });

  const data = asRecord(apiData);
  if (!data) {
    return [emptyApplicant()];
  }

  const summary = asRecord(data['summary']) ?? {};
  const summaryOwner = asRecord(summary['owner']) ?? {};
  const parties = resolvePartiesFromBookingApi(data);

  if (parties.length === 0) {
    const owners = mapOwnersFromBookingApi(data, prefixOptions);
    if (owners.length === 0) {
      return [emptyApplicant()];
    }
    return owners.map((owner) => ({
      partyType: normalizePartyType(owner.partyType),
      name: owner.name ?? '-',
      mobile: owner.mobileNo ?? '-',
      email: owner.email ?? '-',
      pan: owner.panNumber ?? '-',
      aadhar: '—',
      nationality: '',
      residential: {},
      residentialDetails: '—',
    }));
  }

  const mappedOwners = mapOwnersFromBookingApi(data, prefixOptions);

  return parties.map((party, index) => {
    const owner = mappedOwners[index];
    const partyType = normalizePartyType(
      owner?.partyType ?? party['partyType'],
    );
    return {
      partyType,
      name: owner?.name ?? '-',
      mobile: owner?.mobileNo ?? '-',
      email: owner?.email ?? '-',
      pan: owner?.panNumber ?? '-',
      aadhar: aadharFromParty(party, partyType === 'owner' ? summaryOwner : {}),
      nationality: nationalityFromParty(party),
      residential: residentialInputFromParty(party),
      residentialDetails: '—',
    };
  });
}

const AGREEMENT_PROGRESS_STATUSES = new Set([
  'DRAFT',
  'GENERATED',
  'SHARED_WITH_CUSTOMER',
  'REVISE_REQUESTED',
  'CUSTOMER_APPROVED',
  'APPROVED',
]);

const APPROVED_AGREEMENT_STATUSES = new Set(['APPROVED', 'CUSTOMER_APPROVED']);

const PAID_PAYMENT_STATUSES = new Set([
  'PAID',
  'COMPLETED',
  'FULLY_PAID',
  'SUCCESS',
]);

function normalizeAgreementStatus(value: unknown): string {
  return (coerceScalarToString(value) ?? '').toUpperCase();
}

function normalizeRegistrationStatus(value: unknown): string {
  return (coerceScalarToString(value) ?? '').toLowerCase();
}

function isRegistrationPaymentComplete(
  paymentSummary: RegistrationPaymentSummary | null | undefined,
  registration: Record<string, unknown>,
): boolean {
  if (paymentSummary) {
    if (PAID_PAYMENT_STATUSES.has(normalizeAgreementStatus(paymentSummary.paymentStatus))) {
      return true;
    }
    if (paymentSummary.totalAmount > 0 && paymentSummary.pendingAmount <= 0) {
      return true;
    }
  }

  return PAID_PAYMENT_STATUSES.has(
    normalizeAgreementStatus(registration['paymentStatus']),
  );
}

/** Highest completed step index (0–5), aligned with admin registration stepper rules. */
export function resolveRegistrationStepIndex(
  reg: unknown,
  paymentSummary?: RegistrationPaymentSummary | null,
): number {
  const data = asRecord(reg);
  if (!data) {
    return 0;
  }

  const registration = asRecord(data['registration']) ?? {};
  const hasAgreement = Boolean(registration['agreementVersion']);
  const saleAgreementStatus = normalizeAgreementStatus(
    registration['saleAgreementStatus'] ??
      registration['agreementStatus'] ??
      data['saleAgreementStatus'] ??
      data['agreementStatus'] ??
      (hasAgreement ? 'DRAFT' : ''),
  );
  const registrationStatus = normalizeRegistrationStatus(
    registration['registrationStatus'] ?? data['registrationStatus'],
  );

  let stepIndex = 0;

  if (hasAgreement ?? AGREEMENT_PROGRESS_STATUSES.has(saleAgreementStatus)) {
    stepIndex = 1;
  }

  if (APPROVED_AGREEMENT_STATUSES.has(saleAgreementStatus)) {
    stepIndex = 2;
  }

  if (isRegistrationPaymentComplete(paymentSummary, registration)) {
    stepIndex = Math.max(stepIndex, 3);
  }

  if (registrationStatus === 'scheduled' || registrationStatus === 'completed') {
    stepIndex = Math.max(stepIndex, 4);
  }

  if (registrationStatus === 'completed') {
    stepIndex = 5;
  }

  return stepIndex;
}

export function isCustomerAvailabilityConfirmed(reg: unknown): boolean {
  const data = asRecord(reg);
  if (!data) {
    return false;
  }

  const registration = asRecord(data['registration']) ?? data;
  const booleanKeys = [
    'customerAvailabilityConfirmed',
    'availabilityConfirmed',
    'visitConfirmed',
    'isCustomerConfirmed',
    'customerConfirmed',
  ];

  for (const key of booleanKeys) {
    const value = registration[key] ?? data[key];
    if (value === true || value === 'true' || value === 1 || value === '1') {
      return true;
    }
  }

  const timestampKeys = [
    'customerConfirmedAt',
    'availabilityConfirmedAt',
    'visitConfirmedAt',
    'customerAvailabilityConfirmedAt',
  ];

  for (const key of timestampKeys) {
    const value = coerceScalarToString(registration[key] ?? data[key]);
    if (value) {
      return true;
    }
  }

  const confirmedStatuses = new Set([
    'CONFIRMED',
    'CUSTOMER_CONFIRMED',
    'AVAILABLE',
    'SCHEDULED_BY_CUSTOMER',
  ]);

  const visitStatus = normalizeAgreementStatus(
    registration['visitStatus'] ??
      registration['visitConfirmationStatus'] ??
      registration['availabilityStatus'] ??
      data['visitStatus'],
  );

  if (confirmedStatuses.has(visitStatus)) {
    return true;
  }

  const visits = registration['visits'];
  if (Array.isArray(visits)) {
    for (const entry of visits) {
      const visit = asRecord(entry);
      if (!visit) {
        continue;
      }
      const status = normalizeAgreementStatus(
        visit['status'] ?? visit['visitStatus'] ?? visit['confirmationStatus'],
      );
      if (confirmedStatuses.has(status)) {
        return true;
      }
    }
  }

  return false;
}

/** True when registration visit status is completed (visit or nested visits). */
export function isRegistrationVisitCompleted(reg: unknown): boolean {
  const data = asRecord(reg);
  if (!data) {
    return false;
  }

  const registration = asRecord(data['registration']) ?? data;
  const visitStatus = normalizeAgreementStatus(
    registration['visitStatus'] ??
      registration['visitConfirmationStatus'] ??
      registration['availabilityStatus'] ??
      data['visitStatus'],
  );

  if (visitStatus === 'COMPLETED') {
    return true;
  }

  const visits = registration['visits'];
  if (Array.isArray(visits)) {
    for (const entry of visits) {
      const visit = asRecord(entry);
      if (!visit) {
        continue;
      }
      const status = normalizeAgreementStatus(
        visit['status'] ?? visit['visitStatus'] ?? visit['confirmationStatus'],
      );
      if (status === 'COMPLETED') {
        return true;
      }
    }
  }

  return false;
}

export function resolveRegistrationVisitId(reg: unknown): string | null {
  const roots = [asRecord(reg), asRecord(asRecord(reg)?.['data'])].filter(
    (value): value is Record<string, unknown> => !!value,
  );

  for (const data of roots) {
    const registration = asRecord(data['registration']) ?? data;
    const visits = registration['visits'];
    if (Array.isArray(visits)) {
      for (const entry of visits) {
        const visit = asRecord(entry);
        if (!visit) {
          continue;
        }
        const id = visit['id'] ?? visit['_id'] ?? visit['visitId'];
        if (id) {
          return String(id);
        }
      }
    }

    const direct =
      registration['visitId'] ??
      registration['visitScheduleId'] ??
      registration['registrationVisitId'] ??
      data['visitId'] ??
      data['visitScheduleId'] ??
      registration['id'] ??
      registration['_id'];
    if (direct) {
      return String(direct);
    }
  }

  return null;
}

/** Resolve visitStatus from registration payload (top-level or nested visit rows). */
export function resolveVisitStatusFromRegistration(reg: unknown): string | null {
  const data = asRecord(reg);
  if (!data) {
    return null;
  }

  const registration = asRecord(data['registration']) ?? data;
  const directCandidates = [
    registration['visitStatus'],
    registration['visit_status'],
    registration['visitConfirmationStatus'],
    registration['availabilityStatus'],
    data['visitStatus'],
    data['visit_status'],
  ];
  for (const candidate of directCandidates) {
    const value = coerceScalarToString(candidate);
    if (value) {
      return value;
    }
  }

  const visits = registration['visits'];
  if (Array.isArray(visits)) {
    for (const entry of visits) {
      const visit = asRecord(entry);
      if (!visit) {
        continue;
      }
      const nested = coerceScalarToString(
        visit['status'] ?? visit['visitStatus'] ?? visit['confirmationStatus'],
      );
      if (nested) {
        return nested;
      }
    }
  }

  const singleVisit = asRecord(registration['visit']);
  if (singleVisit) {
    const nested = coerceScalarToString(
      singleVisit['status'] ??
        singleVisit['visitStatus'] ??
        singleVisit['confirmationStatus'],
    );
    if (nested) {
      return nested;
    }
  }

  return null;
}

/** Display label for registration visit status from API. */
export function formatVisitStatusLabel(visitStatus: string | null | undefined): string {
  const normalized = String(visitStatus ?? '')
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, '_');
  switch (normalized) {
    case 'SCHEDULED':
      return 'Scheduled';
    case 'RESCHEDULE_REQUESTED':
      return 'Reschedule Requested';
    case 'CONFIRMED':
    case 'CUSTOMER_CONFIRMED':
    case 'AVAILABLE':
    case 'SCHEDULED_BY_CUSTOMER':
      return 'Confirmed';
    case 'COMPLETED':
      return 'Completed';
    case 'PENDING':
      return 'Pending';
    default:
      return normalized
        ? normalized
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/\b\w/g, (c) => c.toUpperCase())
        : 'Pending';
  }
}

export function isRegistrationAlreadyScheduled(reg: unknown): boolean {
  const data = asRecord(reg);
  if (!data) {
    return false;
  }

  const registration = asRecord(data['registration']) ?? data;
  const status = normalizeRegistrationStatus(
    registration['registrationStatus'] ?? data['registrationStatus'],
  );

  if (status === 'scheduled' || status === 'completed') {
    return true;
  }

  return Boolean(
    coerceScalarToString(registration['registrationDate'] ?? data['registrationDate']),
  );
}

export function hasProposedRegistrationSchedule(reg: unknown): boolean {
  const data = asRecord(reg);
  if (!data) {
    return false;
  }

  const registration = asRecord(data['registration']) ?? data;
  return Boolean(
    coerceScalarToString(
      registration['registrationOffice'] ??
        registration['registrarOffice'] ??
        data['registrationOffice'],
    ) ||
      coerceScalarToString(registration['registrationDate'] ?? data['registrationDate']),
  );
}

export function mapPaymentSummaryResponse(raw: unknown): RegistrationPaymentSummary | null {
  const root = asRecord(raw);
  if (!root) {
    return null;
  }

  const data = asRecord(root['data']);
  const nested =
    asRecord(data?.['paymentSummary']) ??
    asRecord(data?.['registrationPaymentSummary']) ??
    asRecord(root['paymentSummary']) ??
    asRecord(root['registrationPaymentSummary']);
  const source = nested ?? data ?? root;

  const hasKnownField = [
    'agreementAmount',
    'agreement_amount',
    'unitAmount',
    'unit_amount',
    'stampDutyAmount',
    'stamp_duty_amount',
    'registrationFeeAmount',
    'registration_fee_amount',
    'totalAmount',
    'total_amount',
    'paidAmount',
    'paid_amount',
    'pendingAmount',
    'pending_amount',
  ].some((key) => source[key] != null);

  if (!hasKnownField) {
    return null;
  }

  const unitAmount = pickNumber(source, 'unitAmount', 'unit_amount');
  const agreementValue = pickNumber(source, 'agreementValue', 'agreement_value', 'agreementAmount', 'agreement_amount');
  const stampDutyAmount = pickNumber(source, 'stampDutyAmount', 'stamp_duty_amount');
  const registrationFeeAmount = pickNumber(
    source,
    'registrationFeeAmount',
    'registration_fee_amount',
  );
  const totalAmount = pickNumber(source, 'totalAmount', 'total_amount');
  const paidAmount = pickNumber(source, 'paidAmount', 'paid_amount');
  const pendingAmount = pickNumber(source, 'pendingAmount', 'pending_amount');

  return {
    registrationId: pickScalarString(source['registrationId'], source['registration_id']),
    unitAmount,
    agreementValue,
    stampDutyPercentage: pickNumber(
      source,
      'stampDutyPercentage',
      'stamp_duty_percentage',
      'stampDutyPercent',
      'stamp_duty_percent',
    ),
    stampDutyAmount,
    registrationFeePercentage: pickNumber(
      source,
      'registrationFeePercentage',
      'registration_fee_percentage',
      'registrationFeePercent',
      'registration_fee_percent',
    ),
    registrationFeeAmount,
    totalAmount,
    paidAmount,
    pendingAmount,
    paymentStatus: pickScalarString(
      source['paymentStatus'],
      source['payment_status'],
      'PENDING',
    ).toUpperCase(),
  };
}

export function buildPaymentSummaryFromBooking(
  reg: Record<string, unknown>,
): RegistrationPaymentSummary | null {
  const summary = asRecord(reg['summary']) ?? {};
  const registration = asRecord(reg['registration']) ?? {};
  const embedded =
    asRecord(reg['registrationPaymentSummary']) ??
    asRecord(reg['paymentSummary']) ??
    asRecord(registration['paymentSummary']) ??
    asRecord(registration['registrationPaymentSummary']);

  const agreementValue =
    pickNumber(embedded ?? {}, 'agreementValue', 'agreement_value', 'agreementAmount', 'agreement_amount') ??
    pickNumber(summary, 'agreementValue', 'agreement_value', 'agreementAmount', 'agreement_amount');

  const unitAmount =
    pickNumber(embedded ?? {}, 'unitAmount', 'unit_amount') ??
    pickNumber(summary, 'totalValue', 'total_value') ??
    pickNumber(asRecord(reg['core']) ?? {}, 'agreementValue', 'agreement_value');

  const stampDutyAmount =
    pickNumber(embedded ?? {}, 'stampDutyAmount', 'stamp_duty_amount') ??
    pickNumber(summary, 'stampDutyAmount', 'stamp_duty_amount') ??
    pickNumber(registration, 'stampDutyAmount', 'stamp_duty_amount');

  const registrationFeeAmount =
    pickNumber(embedded ?? {}, 'registrationFeeAmount', 'registration_fee_amount') ??
    pickNumber(summary, 'registrationFeeAmount', 'registration_fee_amount') ??
    pickNumber(registration, 'registrationFeeAmount', 'registration_fee_amount');

  if (!unitAmount && !stampDutyAmount && !registrationFeeAmount) {
    return embedded ? mapPaymentSummaryResponse(embedded) : null;
  }

  const stampDutyPercentage = pickNumber(
    embedded ?? summary,
    'stampDutyPercentage',
    'stamp_duty_percentage',
    'stampDutyPercent',
    'stamp_duty_percent',
  );
  const registrationFeePercentage = pickNumber(
    embedded ?? summary,
    'registrationFeePercentage',
    'registration_fee_percentage',
    'registrationFeePercent',
    'registration_fee_percent',
  );

  const totalAmount =
    pickNumber(embedded ?? {}, 'totalAmount', 'total_amount') ??
    stampDutyAmount + registrationFeeAmount;
  const paidAmount = pickNumber(embedded ?? {}, 'paidAmount', 'paid_amount');
  const pendingAmount =
    pickNumber(embedded ?? {}, 'pendingAmount', 'pending_amount') ??
    Math.max(0, totalAmount - paidAmount);

  return {
    registrationId: pickScalarString(
      embedded?.['registrationId'],
      embedded?.['registration_id'],
      registration['id'],
    ),
    unitAmount,
    agreementValue,
    stampDutyPercentage: stampDutyPercentage ?? 0,
    stampDutyAmount: stampDutyAmount ?? 0,
    registrationFeePercentage: registrationFeePercentage ?? 0,
    registrationFeeAmount: registrationFeeAmount ?? 0,
    totalAmount,
    paidAmount,
    pendingAmount,
    paymentStatus: pickScalarString(
      embedded?.['paymentStatus'],
      embedded?.['payment_status'],
      'PENDING',
    ).toUpperCase(),
  };
}

export function mapPaymentSummaryToDetail(
  summary: RegistrationPaymentSummary,
  fallbackUnitAmount?: FallbackUnitAmount,
): RegistrationPaymentDisplayDetails {
  const unitAmount = resolveUnitAmountDisplay(summary, fallbackUnitAmount);

  // Format the explicit agreementValue if present, else fallback
  const agreementValueStr = summary.agreementValue > 0 
    ? formatRegistrationInr(summary.agreementValue)
    : formatFallbackUnitAmount(fallbackUnitAmount);

  const stampDutyPercentage =
    summary.stampDutyAmount > 0 && summary.stampDutyPercentage > 0
      ? `${summary.stampDutyPercentage}%`
      : '-';
  const registrationFeePercentage =
    summary.registrationFeeAmount > 0 && summary.registrationFeePercentage > 0
      ? `${summary.registrationFeePercentage}%`
      : '-';

  return {
    unitAmount,
    agreementValue: agreementValueStr,
    stampDutyPercentage,
    stampDutyAmount: formatRegistrationInr(summary.stampDutyAmount),
    registrationFeePercentage,
    registrationFeeAmount: formatRegistrationInr(summary.registrationFeeAmount),
    totalAmount: formatRegistrationInr(summary.totalAmount),
    paidAmount: formatRegistrationInr(summary.paidAmount),
    pendingAmount: formatRegistrationInr(summary.pendingAmount),
    paymentStatus: summary.paymentStatus ?? 'PENDING',
  };
}

export function defaultPaymentDetails(
  fallbackUnitAmount?: FallbackUnitAmount,
): RegistrationPaymentDisplayDetails {
  const unitAmount = formatFallbackUnitAmount(fallbackUnitAmount);

  return {
    unitAmount,
    agreementValue: unitAmount,
    stampDutyPercentage: '-',
    stampDutyAmount: '-',
    registrationFeePercentage: '-',
    registrationFeeAmount: '-',
    totalAmount: '-',
    paidAmount: '-',
    pendingAmount: '-',
    paymentStatus: 'PENDING',
  };
}

export interface RegistrationTdsPaymentSummary {
  tdsAmount: number;
  paidAmount: number;
  pendingAmount: number;
  paymentStatus: string;
}

export interface RegistrationTdsPaymentDisplay {
  tdsAmount: string;
  paidAmount: string;
  pendingAmount: string;
  paymentStatus: string;
}

function resolveTdsPaymentSource(
  reg: Record<string, unknown>,
): Record<string, unknown> | null {
  const registration = asRecord(reg['registration']) ?? {};
  const paymentTotals = asRecord(reg['paymentTotals']) ?? {};
  const summary = asRecord(reg['summary']) ?? {};
  const core = asRecord(reg['core']) ?? {};
  const pricingSnapshot = asRecord(core['pricingSnapshot']) ?? {};

  const embedded =
    asRecord(reg['tdsPaymentSummary']) ??
    asRecord(registration['tdsPaymentSummary']) ??
    asRecord(paymentTotals['tdsPaymentSummary']) ??
    asRecord(summary['tdsPaymentSummary']);

  if (embedded) {
    return embedded;
  }

  // Prefer paymentTotals (get-booking), then root (registration payment-summary).
  const tdsAmount =
    pickNumber(paymentTotals, 'tdsAmount', 'tds_amount', 'tds') ||
    pickNumber(reg, 'tdsAmount', 'tds_amount', 'tds');
  const paidAmount =
    pickNumber(
      paymentTotals,
      'paidTds',
      'paid_tds',
      'tdsPaidAmount',
      'tds_paid_amount',
      'paidTdsAmount',
      'paid_tds_amount',
    ) ||
    pickNumber(
      reg,
      'paidTds',
      'paid_tds',
      'tdsPaidAmount',
      'tds_paid_amount',
      'paidTdsAmount',
      'paid_tds_amount',
    );
  const pendingAmount =
    pickNumber(
      paymentTotals,
      'pendingTds',
      'pending_tds',
      'tdsPendingAmount',
      'tds_pending_amount',
      'pendingTdsAmount',
      'pending_tds_amount',
    ) ||
    pickNumber(
      reg,
      'pendingTds',
      'pending_tds',
      'tdsPendingAmount',
      'tds_pending_amount',
      'pendingTdsAmount',
      'pending_tds_amount',
    );

  if (tdsAmount || paidAmount || pendingAmount) {
    const resolvedPending =
      pendingAmount || Math.max(0, tdsAmount - paidAmount);
    return {
      tdsAmount,
      paidAmount,
      pendingAmount: resolvedPending,
      paymentStatus:
        resolvedPending <= 0 && tdsAmount > 0 ? 'PAID' : 'PENDING',
    };
  }

  const summaryTdsAmount = pickNumber(summary, 'tdsAmount', 'tds_amount', 'tds');
  const snapshotTdsAmount = pickNumber(pricingSnapshot, 'tds_amount', 'tdsAmount');
  const resolvedTdsAmount = summaryTdsAmount || snapshotTdsAmount;
  if (!resolvedTdsAmount) {
    return null;
  }

  const summaryPaidAmount = pickNumber(
    summary,
    'paidTds',
    'paid_tds',
    'tdsPaidAmount',
    'tds_paid_amount',
  );
  const summaryPendingAmount =
    pickNumber(
      summary,
      'pendingTds',
      'pending_tds',
      'tdsPendingAmount',
      'tds_pending_amount',
    ) || Math.max(0, resolvedTdsAmount - summaryPaidAmount);

  return {
    tdsAmount: resolvedTdsAmount,
    paidAmount: summaryPaidAmount,
    pendingAmount: summaryPendingAmount,
    paymentStatus:
      summaryPendingAmount <= 0 && resolvedTdsAmount > 0 ? 'PAID' : 'PENDING',
  };
}

export function mapTdsPaymentSummaryFromBooking(
  reg: Record<string, unknown> | null | undefined,
): RegistrationTdsPaymentSummary | null {
  if (!reg) {
    return null;
  }

  const source = resolveTdsPaymentSource(reg);
  if (!source) {
    return null;
  }

  const tdsAmount = pickNumber(source, 'tdsAmount', 'tds_amount', 'tds');
  const paidAmount = pickNumber(
    source,
    'paidAmount',
    'paid_amount',
    'paidTds',
    'paid_tds',
    'tdsPaidAmount',
    'tds_paid_amount',
  );
  const pendingAmount =
    pickNumber(
      source,
      'pendingAmount',
      'pending_amount',
      'pendingTds',
      'pending_tds',
      'tdsPendingAmount',
      'tds_pending_amount',
    ) || Math.max(0, tdsAmount - paidAmount);

  if (!tdsAmount && !paidAmount && !pendingAmount) {
    return null;
  }

  const paymentStatus = pickScalarString(
    source['paymentStatus'],
    source['payment_status'],
    pendingAmount <= 0 && tdsAmount > 0 ? 'PAID' : 'PENDING',
  ).toUpperCase();

  return {
    tdsAmount,
    paidAmount,
    pendingAmount,
    paymentStatus,
  };
}

export function mapTdsPaymentSummaryResponse(
  raw: unknown,
): RegistrationTdsPaymentSummary | null {
  const root = asRecord(raw);
  if (!root) {
    return null;
  }

  const data = asRecord(root['data']);
  const nested =
    asRecord(data?.['tdsPaymentSummary']) ??
    asRecord(root['tdsPaymentSummary']) ??
    asRecord(data) ??
    root;

  // Registration payment-summary returns paidTds / pendingTds at the root.
  const direct = mapTdsPaymentSummaryFromBooking({
    ...nested,
    paymentTotals: {
      tdsAmount: nested['tdsAmount'] ?? nested['tds_amount'],
      paidTds: nested['paidTds'] ?? nested['paid_tds'],
      pendingTds: nested['pendingTds'] ?? nested['pending_tds'],
    },
  });
  if (direct) {
    return direct;
  }

  return mapTdsPaymentSummaryFromBooking(nested);
}

export function mapTdsPaymentDisplay(
  summary: RegistrationTdsPaymentSummary | null | undefined,
): RegistrationTdsPaymentDisplay {
  if (!summary) {
    return {
      tdsAmount: '—',
      paidAmount: '—',
      pendingAmount: '—',
      paymentStatus: 'PENDING',
    };
  }

  return {
    tdsAmount: summary.tdsAmount > 0 ? formatRegistrationInr(summary.tdsAmount) : '—',
    paidAmount: formatRegistrationInr(summary.paidAmount),
    pendingAmount: formatRegistrationInr(summary.pendingAmount),
    paymentStatus: summary.paymentStatus,
  };
}

export interface FinalAgreementSyncedDocumentConfig {
  id: string;
  title: string;
  documentTypes: readonly string[];
}

export interface FinalAgreementSyncedDocumentView {
  id: string;
  title: string;
  fileKey: string | null;
  fileName: string | null;
  isAvailable: boolean;
}

/** Synced with admin Final Agreement and Loan Documents → Applicant. */
export const FINAL_AGREEMENT_SYNCED_DOCUMENTS: readonly FinalAgreementSyncedDocumentConfig[] =
  [
    {
      id: 'salesRegistration',
      title: 'Sales Registration Document',
      documentTypes: [
        'final_agreement',
        'FINAL_AGREEMENT',
        'sales_registration',
        'executed_agreement',
      ],
    },
    {
      id: 'noc',
      title: 'No Objection Certificate',
      documentTypes: ['agreement_loan_noc', 'AGREEMENT_LOAN_NOC'],
    },
    {
      id: 'bankReleaseNoc',
      title: 'Bank Release NOC',
      documentTypes: ['bank_release_noc', 'BANK_RELEASE_NOC'],
    },
  ];

function normalizeFinalAgreementDocumentType(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/-/g, '_');
}

export function findBookingDocumentByTypes(
  docs: unknown[],
  documentTypes: readonly string[],
): Record<string, unknown> | null {
  if (!Array.isArray(docs)) {
    return null;
  }

  const normalizedTypes = new Set(
    documentTypes.map(type => normalizeFinalAgreementDocumentType(type)),
  );

  const match = docs.find(entry => {
    if (!entry || typeof entry !== 'object') {
      return false;
    }
    const doc = entry as Record<string, unknown>;
    const type = normalizeFinalAgreementDocumentType(
      doc['documentType'] ?? doc['type'] ?? doc['document_type'],
    );
    return normalizedTypes.has(type);
  });

  return match && typeof match === 'object' ? (match as Record<string, unknown>) : null;
}

export function pickBookingDocumentFileKey(
  doc: Record<string, unknown> | null | undefined,
): string | null {
  if (!doc) {
    return null;
  }
  const key =
    doc['fileUrl'] ??
    doc['file_url'] ??
    doc['url'] ??
    doc['key'] ??
    doc['documentUrl'] ??
    doc['document_url'] ??
    doc['documentKey'] ??
    doc['document_key'] ??
    doc['storageKey'] ??
    doc['storage_key'];
  const normalized = String(key ?? '').trim();
  return normalized || null;
}
